import type { ShopProduct } from '@/features/shop/types';
import type { UnavailableReason } from '@/features/product-variant/types';
/**
 * Stock: the vendor's count, and the shopper's watch.
 *
 * Two audiences, two route trees — a vendor writes counts under /vendor, a shopper
 * registers interest under /user. Kept in one module because they are two halves of the
 * same story, and reading them together is how the flow stays coherent.
 */
import { api } from '@/lib/api';

export type Vertical = 'FOOD' | 'PHARMACY';

export type StockSaveResult = {
  vertical: Vertical;
  variant_id: string;
  stock_qty: number | null;
  is_in_stock: boolean;
  is_low_stock: boolean;
  is_tracked: boolean;
  /** How many waiting shoppers this restock just notified. */
  watchers_notified: number;
};

/** Null stops tracking, and reads as always available from then on. */
export async function set_variant_stock(
  vertical: Vertical,
  variant_id: string,
  stock_qty: number | null,
): Promise<StockSaveResult> {
  const res = await api.put<StockSaveResult>(`/vendor/stock/${vertical}/${variant_id}`, { stock_qty });
  return res.data;
}

export type StockAlertResult = {
  /**
   * True when the shopper could simply have bought it — a stale page, not an error.
   *
   * Purchasability, not the raw count: a size that is in stock under a paused listing is NOT
   * available, and reports false here.
   */
  already_available?: boolean;
  /** Deprecated alias for `already_available`, kept because shipped clients read it. */
  already_in_stock: boolean;
  watching: boolean;
  is_in_stock: boolean;
  is_purchasable?: boolean;
  unavailable_reason?: UnavailableReason | null;
};

export async function watch_variant(vertical: Vertical, variant_id: string): Promise<StockAlertResult> {
  const res = await api.post<StockAlertResult>('/user/stock-alerts', { vertical, variant_id });
  return res.data;
}

export async function unwatch_variant(variant_id: string): Promise<void> {
  await api.delete(`/user/stock-alerts/${variant_id}`);
}

export type StockAlert = {
  variant_id: string;
  vertical: Vertical;
  /** Human-readable description of the watched item — "Acme Kibble (3 kg)". */
  label: string;
  /** Just the size. Null once the variant has been deleted. */
  variant_label: string | null;
  is_in_stock: boolean;
  /** Of the WATCHED SIZE — the product being buyable in another size is a different question. */
  is_purchasable: boolean;
  /** Also carries `NO_LONGER_SOLD` when the watched variant has been deleted. */
  unavailable_reason: UnavailableReason | 'NO_LONGER_SOLD' | null;
  /**
   * The full product card, so the watchlist renders from one payload. Null once the product
   * has been deleted — the alert row is kept, because "no longer sold" beats it vanishing.
   */
  product: ShopProduct | null;
  /** Null while still waiting. */
  notified_at: string | null;
  created_at: string;
};

export async function fetch_stock_alerts(): Promise<StockAlert[]> {
  const res = await api.get<{ items: StockAlert[] }>('/user/stock-alerts');
  return res.data.items;
}
